import React, { useEffect, useState } from 'react';
import { Loader2, Scale, Lock, Check, CalendarIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Calendar } from '../ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { bookingsApi } from '../../services/api';
import { formatDate } from '../../lib/utils';
import { toast } from 'sonner';

function blank(c) {
  return { container_number: c.number, vgm_kg: c.vgm_kg || '', method: c.vgm_method || 'SM1', weighing_party: c.vgm_weighing_party || '', weighed_at: (c.vgm_weighed_at || '').slice(0, 10), authorised_person: c.vgm_authorised_person || '', reference: c.vgm_reference || '' };
}

// SOLAS VI/2 verified gross mass, one row per assigned container.
export function VgmSection({ bookingId, onDeclared }) {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(function () {
    bookingsApi.getVgm(bookingId).then(function (r) { setData(r.data); setRows(r.data.containers.map(blank)); }).catch(function () { setData(null); });
  }, [bookingId]);

  if (!data || data.shipment_mode === 'bulk') return null;

  function set(i, k, v) { setRows(function (p) { return p.map(function (r, j) { return j === i ? { ...r, [k]: v } : r; }); }); }
  function applyToAll(k) { setRows(function (p) { return p.map(function (r) { return { ...r, [k]: p[0][k] }; }); }); }

  async function submit() {
    const filled = rows.filter(function (r) { return r.vgm_kg; });
    if (!filled.length) { toast.error('Enter the verified gross mass for at least one container'); return; }
    const bad = filled.find(function (r) { return !r.weighing_party || !r.weighed_at || !r.authorised_person; });
    if (bad) { toast.error(bad.container_number + ': weighing party, date and authorised person are required'); return; }
    const over = filled.find(function (r) { return Number(r.vgm_kg) > data.max_gross_kg; });
    if (over) { toast.error(over.container_number + ': exceeds the maximum gross mass of ' + data.max_gross_kg.toLocaleString() + ' kg'); return; }
    setSaving(true);
    try {
      const res = await bookingsApi.submitVgm(bookingId, filled.map(function (r) { return { ...r, vgm_kg: Number(r.vgm_kg), reference: r.reference || null }; }));
      toast.success('VGM declared for ' + filled.length + ' container(s)' + (res.data.complete ? ' — SOLAS VGM certificate is now available' : ''));
      const refreshed = await bookingsApi.getVgm(bookingId);
      setData(refreshed.data); setRows(refreshed.data.containers.map(blank));
      if (onDeclared) onDeclared(refreshed.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not save the VGM declaration');
    } finally {
      setSaving(false);
    }
  }

  const complete = data.total > 0 && data.declared === data.total;
  return (
    <div className="bg-white rounded-sm shadow-card p-6" data-testid="vgm-section">
      <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-3 mb-4">
        <div>
          <h3 className="font-medium text-ink flex items-center gap-2"><Scale className="h-4 w-4 text-blue-600" /> Verified gross mass (SOLAS VI/2)</h3>
          <p className="text-sm text-gray-600 mt-1">Declare the verified gross mass of each packed container before the VGM cut-off. Method 1: weigh the packed container. Method 2: weigh cargo, packaging and dunnage and add the container tare ({data.tare_kg.toLocaleString()} kg). Maximum gross {data.max_gross_kg.toLocaleString()} kg.</p>
        </div>
        <div className="text-sm text-right space-y-1 shrink-0">
          <p className="text-gray-700">VGM cut-off <span className="sv-mono text-ink" data-testid="vgm-cutoff">{formatDate(data.cutoff)}</span></p>
          <p className={complete ? 'text-success-700 inline-flex items-center gap-1' : 'text-amber-800'} data-testid="vgm-progress">{complete && <Check className="h-4 w-4" />}{data.declared} of {data.total} declared</p>
          {data.locked && <p className="inline-flex items-center gap-1.5 text-gray-600"><Lock className="h-4 w-4" /> Vessel sailed — locked</p>}
        </div>
      </div>
      {data.total === 0 ? (
        <p className="text-sm text-gray-500" data-testid="vgm-no-containers">Containers are assigned by Südvik once the booking is confirmed; VGM can be declared after that.</p>
      ) : (
        <fieldset disabled={data.locked} className="disabled:opacity-70">
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="vgm-table">
              <thead>
                <tr className="text-left text-xs text-gray-500 uppercase tracking-wide">
                  <th className="py-2 pr-3">Container</th><th className="py-2 pr-3">VGM (kg)</th><th className="py-2 pr-3">Method</th><th className="py-2 pr-3">Weighing party</th><th className="py-2 pr-3">Weighed on</th><th className="py-2 pr-3">Authorised person</th><th className="py-2">Ticket / ref</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(function (r, i) {
                  const c = data.containers[i];
                  return (
                    <tr key={r.container_number} data-testid={'vgm-row-' + r.container_number}>
                      <td className="py-2 pr-3 whitespace-nowrap"><span className="sv-mono">{r.container_number}</span><div className="text-xs text-gray-500">{c.iso_code} · tare {Number(c.tare_kg).toLocaleString()} kg{c.vgm_declared_at ? ' · declared ' + formatDate(c.vgm_declared_at) : ''}</div></td>
                      <td className="py-2 pr-3"><Input type="number" min="1" step="1" value={r.vgm_kg} onChange={function (e) { set(i, 'vgm_kg', e.target.value); }} className="h-9 w-28 rounded-sm sv-mono" data-testid={'vgm-kg-' + i} /></td>
                      <td className="py-2 pr-3"><select value={r.method} onChange={function (e) { set(i, 'method', e.target.value); }} className="h-9 rounded-sm border border-input bg-background px-2 text-sm" data-testid={'vgm-method-' + i}><option value="SM1">Method 1</option><option value="SM2">Method 2</option></select></td>
                      <td className="py-2 pr-3"><Input value={r.weighing_party} onChange={function (e) { set(i, 'weighing_party', e.target.value); }} placeholder="e.g. Port weighbridge" className="h-9 w-44 rounded-sm" data-testid={'vgm-party-' + i} /></td>
                      <td className="py-2 pr-3">
                        <Popover>
                          <PopoverTrigger asChild>
                            <Button type="button" variant="outline" className="h-9 w-36 rounded-sm justify-start font-normal sv-mono text-sm px-2" data-testid={'vgm-date-' + i} data-value={r.weighed_at}>
                              <CalendarIcon className="mr-2 h-3.5 w-3.5 text-gray-500 shrink-0" />{r.weighed_at ? format(parseISO(r.weighed_at), 'dd MMM yyyy') : 'Pick date'}
                            </Button>
                          </PopoverTrigger>
                          <PopoverContent className="w-auto p-0" align="start">
                            <Calendar mode="single" selected={r.weighed_at ? parseISO(r.weighed_at) : undefined} defaultMonth={r.weighed_at ? parseISO(r.weighed_at) : undefined} onSelect={function (d) { if (d) set(i, 'weighed_at', format(d, 'yyyy-MM-dd')); }} disabled={function (d) { return d > new Date(new Date().setHours(23, 59, 59, 999)); }} initialFocus />
                          </PopoverContent>
                        </Popover>
                      </td>
                      <td className="py-2 pr-3"><Input value={r.authorised_person} onChange={function (e) { set(i, 'authorised_person', e.target.value); }} placeholder="Name in capitals" className="h-9 w-40 rounded-sm" data-testid={'vgm-person-' + i} /></td>
                      <td className="py-2"><Input value={r.reference} onChange={function (e) { set(i, 'reference', e.target.value); }} placeholder="Optional" className="h-9 w-28 rounded-sm sv-mono" data-testid={'vgm-ref-' + i} /></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mt-4">
            {rows.length > 1 ? <button type="button" className="text-sm sv-link" onClick={function () { ['method', 'weighing_party', 'weighed_at', 'authorised_person'].forEach(applyToAll); }} data-testid="vgm-copy-first">Copy method, party, date and person from the first row to all</button> : <span />}
            {!data.locked && <Button type="button" onClick={submit} disabled={saving} className="rounded-sm bg-ink hover:bg-ink-800" data-testid="vgm-submit">{saving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Declare VGM'}</Button>}
          </div>
        </fieldset>
      )}
    </div>
  );
}
